/**
 * Design Library — industry patterns (design knowledge specific to a trade).
 * Pure design knowledge: no pixels, geometry, or editor concepts.
 */

import { definePattern } from './_define';

export const INDUSTRY_PATTERNS = [
  definePattern({
    id: 'restaurant-menu', name: 'Restaurant Menu', family: 'industry',
    purpose: 'Dishes organised so they can be chosen quickly and hungrily.',
    communicationGoal: 'Make choosing easy and appetising.',
    visualGoal: 'Clear categories with a readable price rhythm.',
    contentGoal: 'Category, dish, description, price; optional QR.',
    marketingGoal: 'Raise average order value with a featured dish.',
    idealDocuments: ['Menu', 'Flyer', 'Pamphlet', 'Poster'],
    idealIndustries: ['restaurant', 'cafe', 'bakery', 'food', 'bar-pub'],
    idealAudiences: ['general-public', 'families', 'tourists'],
    idealGenomes: ['organic', 'editorial', 'retro', 'luxury', 'bold'],
    compatiblePatterns: ['qr-block', 'image-band', 'offer-section'],
    compositionPhilosophy: 'Category blocks with a consistent price alignment.',
    hierarchyPhilosophy: 'Category, then dish, then price; one dish may be featured.',
    typicalSections: ['category', 'dish', 'description', 'price'],
    preferredComponents: ['menu', 'pricing', 'contact'],
    premiumCues: ['refined-type', 'crafted-detail'],
    thingsToAvoid: ['clutter', 'inconsistent-price-alignment', 'low-quality-food-photos'],
    variationRules: ['category-count', 'photo-or-typographic', 'featured-dish'],
    capabilities: ['menu', 'pricing', 'list', 'qr', 'image'],
    tags: ['menu', 'food', 'industry'],
    expansionRules: { emphasis: 'categories', alignment: 'price-column' },
  }),
  definePattern({
    id: 'medical-services', name: 'Medical Services', family: 'industry',
    purpose: 'Clinical services presented with calm authority.',
    communicationGoal: 'Reassure while explaining what is offered.',
    visualGoal: 'Airy, clean, unmistakably trustworthy.',
    contentGoal: 'Service name, short explanation, icon, booking action.',
    marketingGoal: 'Convert concern into an appointment.',
    idealDocuments: ['Flyer', 'Brochure', 'Poster', 'Pamphlet'],
    idealIndustries: ['medical', 'dental', 'veterinary', 'pharmacy', 'yoga-wellness'],
    idealAudiences: ['families', 'general-public', 'professionals'],
    idealGenomes: ['premium-medical', 'minimal', 'corporate', 'scandinavian'],
    compatiblePatterns: ['feature-row', 'contact-section', 'testimonials', 'premium-cta'],
    compositionPhilosophy: 'Generous whitespace; services on a calm, even grid.',
    hierarchyPhilosophy: 'Reassurance first, services second, booking always visible.',
    typicalSections: ['service-name', 'service-copy', 'icon', 'cta'],
    preferredComponents: ['features', 'cta', 'contact'],
    premiumCues: ['precision', 'generous-whitespace', 'considered-color'],
    thingsToAvoid: ['clutter', 'alarming-imagery', 'clip-art'],
    variationRules: ['service-count', 'icon-style'],
    capabilities: ['icon-list', 'cta', 'contact', 'list'],
    tags: ['medical', 'services', 'industry'],
    expansionRules: { emphasis: 'reassurance', density: 'airy' },
  }),
  definePattern({
    id: 'real-estate-listing', name: 'Real Estate Listing', family: 'industry',
    purpose: 'A property presented with aspiration and hard facts.',
    communicationGoal: 'Sell the lifestyle, then prove the specifics.',
    visualGoal: 'A commanding property image above precise detail.',
    contentGoal: 'Hero image, price, key specs, location, agent contact.',
    marketingGoal: 'Drive a viewing.',
    idealDocuments: ['Flyer', 'Brochure', 'Poster', 'Instagram Post'],
    idealIndustries: ['real-estate', 'construction', 'interior-design', 'architecture'],
    idealAudiences: ['families', 'professionals', 'luxury-customers'],
    idealGenomes: ['luxury', 'editorial', 'corporate', 'modern'],
    compatiblePatterns: ['split-hero', 'image-band', 'stat-row', 'contact-section'],
    compositionPhilosophy: 'Image-led above a precise, tabular fact zone.',
    hierarchyPhilosophy: 'Image, then price, then specs, then the agent.',
    typicalSections: ['image', 'price', 'specs', 'location', 'agent'],
    preferredComponents: ['hero', 'stats', 'contact'],
    premiumCues: ['refined-type', 'confident-scale', 'precision'],
    thingsToAvoid: ['low-quality-imagery', 'hidden-price', 'clutter'],
    variationRules: ['spec-count', 'gallery-or-single-image'],
    capabilities: ['image', 'stats', 'contact', 'image-led'],
    tags: ['property', 'listing', 'industry'],
    expansionRules: { emphasis: 'image', factZone: 'tabular' },
  }),
  definePattern({
    id: 'wedding-invitation', name: 'Wedding Invitation', family: 'industry',
    purpose: 'An invitation carrying romance and ceremony.',
    communicationGoal: 'Invite warmly and give the essential details.',
    visualGoal: 'Soft, graceful, symmetric, delicate.',
    contentGoal: 'Names, date, venue, RSVP.',
    marketingGoal: 'Set the tone of the day and secure the reply.',
    idealDocuments: ['Invitation', 'Flyer', 'Instagram Post'],
    idealIndustries: ['wedding', 'events', 'photography'],
    idealAudiences: ['families', 'women', 'general-public'],
    idealGenomes: ['editorial', 'organic', 'luxury', 'minimal'],
    compatiblePatterns: ['minimal-stack', 'centered-poster', 'image-band'],
    incompatiblePatterns: ['collage-wall', 'discount-block'],
    compositionPhilosophy: 'Centred and airy; delicate motifs frame the names.',
    hierarchyPhilosophy: 'Names first, date second, venue and RSVP third.',
    typicalSections: ['names', 'date', 'venue', 'rsvp'],
    preferredComponents: ['hero', 'contact'],
    premiumCues: ['crafted-detail', 'refined-type', 'generous-whitespace'],
    thingsToAvoid: ['clutter', 'clip-art', 'low-contrast'],
    variationRules: ['motif-density', 'script-or-serif'],
    capabilities: ['text-led', 'image', 'contact'],
    tags: ['wedding', 'invitation', 'industry'],
    expansionRules: { emphasis: 'names', axis: 'center' },
  }),
  definePattern({
    id: 'festival-banner', name: 'Festival Banner', family: 'industry',
    purpose: 'A celebratory banner announcing a festival or event.',
    communicationGoal: 'Radiate energy and give the essentials.',
    visualGoal: 'Vibrant, expressive, unmissable.',
    contentGoal: 'Event name, dates, lineup or highlight, ticket action.',
    marketingGoal: 'Sell passes and build buzz.',
    idealDocuments: ['Banner', 'Poster', 'Instagram Post', 'YouTube Banner'],
    idealIndustries: ['festival', 'events', 'music', 'religious'],
    idealAudiences: ['students', 'families', 'general-public'],
    idealGenomes: ['memphis', 'bold', 'gradient-mesh', 'retro'],
    compatiblePatterns: ['collage-wall', 'poster-hero', 'discount-block', 'premium-cta'],
    incompatiblePatterns: ['minimal-stack', 'business-card-layout'],
    compositionPhilosophy: 'Maximal energy anchored by one dominant name.',
    hierarchyPhilosophy: 'Event name, then dates, then lineup, then tickets.',
    typicalSections: ['event-name', 'dates', 'lineup', 'cta'],
    preferredComponents: ['hero', 'cta'],
    premiumCues: ['confident-scale', 'crafted-detail'],
    thingsToAvoid: ['illegible-overlap', 'low-contrast'],
    variationRules: ['decoration-density', 'lineup-length'],
    capabilities: ['cta', 'image', 'schedule', 'fullbleed'],
    tags: ['festival', 'event', 'industry'],
    expansionRules: { emphasis: 'event-name', density: 'dense' },
  }),
  definePattern({
    id: 'travel-destination-card', name: 'Travel Destination Card', family: 'industry',
    purpose: 'A destination sold as a feeling with a price.',
    communicationGoal: 'Trigger wanderlust, then make it bookable.',
    visualGoal: 'Vivid destination imagery with a clean fact strip.',
    contentGoal: 'Destination, image, price-from, duration, action.',
    marketingGoal: 'Convert daydreaming into a booking.',
    idealDocuments: ['Flyer', 'Instagram Post', 'Brochure', 'Banner'],
    idealIndustries: ['travel', 'hotel', 'events'],
    idealAudiences: ['tourists', 'families', 'students'],
    idealGenomes: ['gradient-mesh', 'modern', 'editorial', 'organic'],
    compatiblePatterns: ['image-band', 'card-stack', 'premium-cta', 'offer-section'],
    compositionPhilosophy: 'Image dominates; facts sit in a quiet strip beneath.',
    hierarchyPhilosophy: 'Destination, then image, then price, then action.',
    typicalSections: ['destination', 'image', 'price', 'duration', 'cta'],
    preferredComponents: ['hero', 'pricing', 'cta'],
    premiumCues: ['considered-color', 'confident-scale'],
    thingsToAvoid: ['stock-cliches', 'hidden-price'],
    variationRules: ['card-count', 'price-prominence'],
    capabilities: ['image', 'pricing', 'cta', 'image-led'],
    tags: ['travel', 'destination', 'industry'],
    expansionRules: { emphasis: 'image', factStrip: 'quiet' },
  }),
];
